import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';

const EMPTY_FORM = () => ({
  baustelle_id: '', datum: new Date().toISOString().split('T')[0],
  bauleiter: '', temperatur: '', wetter: '', wind: '',
  niederschlag: '', sichtweite: '', wetter_bem: '',
  arbeitsbeginn: '', arbeitsende: '', std_gesamt: '',
  anzahl_monteure: '', gesamtstunden: '',
  leistungen: '', stillstand: false, stillstand_grund: '',
  foto_doku: false, anfahrt: false, status: 'entwurf',
  sig_ersteller: '', sig_bauleiter: '', material: [],
});

// ── Signature Pad ─────────────────────────────────────────────────────────────
function SignaturePad({ label, value, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasSig, setHasSig] = useState(false);

  useEffect(() => {
    if (value && canvasRef.current) {
      const img = new Image();
      img.onload = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, 400, 120);
        ctx.drawImage(img, 0, 0);
        setHasSig(true);
      };
      img.src = value;
    }
  }, [value]);

  const pos = (e, canvas) => {
    const r = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - r.left) * (400 / r.width), y: (src.clientY - r.top) * (120 / r.height) };
  };

  const start = e => { e.preventDefault(); drawing.current = true; const p = pos(e, canvasRef.current); canvasRef.current.getContext('2d').beginPath(); canvasRef.current.getContext('2d').moveTo(p.x, p.y); };
  const draw = e => { e.preventDefault(); if (!drawing.current) return; const ctx = canvasRef.current.getContext('2d'); ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#003366'; const p = pos(e, canvasRef.current); ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const stop = e => { e.preventDefault(); if (!drawing.current) return; drawing.current = false; setHasSig(true); onChange(canvasRef.current.toDataURL()); };
  const clear = () => { canvasRef.current.getContext('2d').clearRect(0, 0, 400, 120); setHasSig(false); onChange(''); };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <label className="form-label">{label}</label>
        {hasSig && <button type="button" onClick={clear} style={{ fontSize: 11, color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer' }}>Löschen</button>}
      </div>
      <canvas ref={canvasRef} width={400} height={120}
        style={{ width: '100%', height: 120, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'white', cursor: 'crosshair', touchAction: 'none' }}
        onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
        onTouchStart={start} onTouchMove={draw} onTouchEnd={stop} />
      {!hasSig && <div style={{ marginTop: -60, textAlign: 'center', color: 'var(--text-3)', fontSize: 12, pointerEvents: 'none', paddingBottom: 40 }}>Hier unterschreiben</div>}
    </div>
  );
}

// ── Main Form ─────────────────────────────────────────────────────────────────
export default function BautagebuchForm() {
  const { id: rawId } = useParams();
  const id = (rawId && rawId !== 'erstellen') ? rawId : null;
  // Note: this form is used for /erstellen and /:id/bearbeiten
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState(EMPTY_FORM());
  const [baustellen, setBaustellen] = useState([]);
  const [katalog, setKatalog] = useState([]);
  const [fotos, setFotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [wetterLoading, setWetterLoading] = useState(false);
  const [msg, setMsg] = useState('');

  // Load data
  useEffect(() => {
    api.get('/baustellen').then(r => setBaustellen(r.data)).catch(() => {});
    api.get('/material').then(r => setKatalog(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isEdit && id) {
      api.get(`/bautagebuecher/${id}`).then(r => {
        const d = r.data;
        setForm({
          ...EMPTY_FORM(), ...d,
          stillstand: !!d.stillstand, foto_doku: !!d.foto_doku, anfahrt: !!d.anfahrt,
          sig_ersteller: d.sig_ersteller || '', sig_bauleiter: d.sig_bauleiter || '',
          material: (d.material || []).map(m => ({ ...m })),
        });
        
        setFotos(d.fotos || []);
      }).catch(() => {});
    } else {
      setForm(f => ({ ...EMPTY_FORM(), bauleiter: user?.name || '' }));
      setFotos([]);
    }
  }, [id, isEdit]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setMat = (i, k, v) => setForm(f => { const m = [...f.material]; m[i] = { ...m[i], [k]: v }; return { ...f, material: m }; });

  // Auto-calc hours
  const calcHours = (beg, end, n) => {
    if (!beg || !end) return;
    const [h1, m1] = beg.split(':').map(Number);
    const [h2, m2] = end.split(':').map(Number);
    const diff = parseFloat(((h2 * 60 + m2 - h1 * 60 - m1) / 60).toFixed(1));
    if (diff <= 0) return;
    const monteure = parseFloat(n || form.anzahl_monteure) || 1;
    setForm(f => ({ ...f, std_gesamt: diff.toString(), gesamtstunden: (diff * monteure).toFixed(1) }));
  };

  // Auto-weather
  const loadWetter = () => {
    if (!navigator.geolocation) return;
    setWetterLoading(true);
    navigator.geolocation.getCurrentPosition(async pos => {
      try {
        const r = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${pos.coords.latitude}&longitude=${pos.coords.longitude}&current=temperature_2m,weathercode,windspeed_10m,precipitation&timezone=auto`);
        const d = await r.json();
        const codes = { 0:'Klar',1:'Meist klar',2:'Teilweise bewölkt',3:'Bedeckt',45:'Nebel',48:'Nebel',51:'Nieselregen',61:'Leichter Regen',63:'Regen',65:'Starkregen',71:'Leichter Schnee',73:'Schnee',80:'Schauer',81:'Schauer',95:'Gewitter' };
        setForm(f => ({ ...f, temperatur: `${Math.round(d.current.temperature_2m)}°C`, wetter: codes[d.current.weathercode] || 'Unbekannt', wind: `${Math.round(d.current.windspeed_10m)} km/h`, niederschlag: d.current.precipitation > 0 ? `${d.current.precipitation} mm` : 'Kein Niederschlag' }));
      } catch {}
      setWetterLoading(false);
    }, () => setWetterLoading(false));
  };

  // Save - returns the id
  const doSave = async (status) => {
    const payload = { ...form, status };
    if (isEdit && id) {
      await api.put(`/bautagebuecher/${id}`, payload);
      return id;
    } else {
      const r = await api.post('/bautagebuecher', payload);
      return r.data.id;
    }
  };

  const save = async (status) => {
    setSaving(true); setMsg('');
    try {
      const newId = await doSave(status);
      setMsg('Gespeichert ✓');
      setTimeout(() => navigate(`/bautagebuch/${newId}/ansicht`), 600);
    } catch { setMsg('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  // Foto upload - saves first if needed
  // PDF export with token
  const exportPdf = () => {
    const token = localStorage.getItem('token');
    const bid = id;
    if (!bid) { alert('Bitte erst speichern'); return; }
    const url = `/api/bautagebuecher/${bid}/pdf?token=${token}`;
    window.open(url, '_blank');
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">{isEdit ? 'Bautagebuch bearbeiten' : 'Neues Bautagebuch'}</div>
          <div className="page-sub">{id ? `#${id}` : 'Neuer Eintrag'}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => navigate('/bautagebuch')}>Abbrechen</button>
          {isEdit && id && <button className="btn btn-secondary" onClick={exportPdf}>📄 PDF</button>}
          <button className="btn btn-secondary" onClick={() => save('entwurf')} disabled={saving}>Entwurf</button>
          <button className="btn btn-primary" onClick={() => save('fertig')} disabled={saving}>{saving ? 'Speichert…' : 'Abschließen'}</button>
        </div>
      </div>

      {msg && <div style={{ background: msg.includes('Fehler') ? 'var(--red-bg)' : 'var(--green-bg)', color: msg.includes('Fehler') ? 'var(--red)' : 'var(--green)', padding: '10px 16px', borderRadius: 'var(--radius)', marginBottom: 16, fontSize: 13, fontWeight: 500 }}>{msg}</div>}

      {/* Kopfdaten */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="form-section">Kopfdaten</div>
        <div className="form-grid form-grid-3" style={{ marginBottom: 14 }}>
          <div className="form-group"><label className="form-label">Datum</label><input className="form-input" type="date" value={form.datum} onChange={e => set('datum', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Baustelle</label>
            <select className="form-select" value={form.baustelle_id} onChange={e => set('baustelle_id', e.target.value)}>
              <option value="">— wählen —</option>
              {baustellen.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div className="form-group"><label className="form-label">Bauleiter / Polier</label><input className="form-input" value={form.bauleiter} onChange={e => set('bauleiter', e.target.value)} /></div>
        </div>

        <div className="form-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Witterungsbedingungen</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={loadWetter} disabled={wetterLoading}>{wetterLoading ? '⏳ Lädt…' : '🌤 Auto'}</button>
        </div>
        <div className="form-grid form-grid-3" style={{ marginBottom: 14 }}>
          {[['Temperatur (°C)', 'temperatur'], ['Wetter', 'wetter'], ['Wind', 'wind'], ['Niederschlag', 'niederschlag'], ['Sichtweite', 'sichtweite'], ['Besonderheiten', 'wetter_bem']].map(([l, k]) => (
            <div className="form-group" key={k}><label className="form-label">{l}</label><input className="form-input" value={form[k]} onChange={e => set(k, e.target.value)} /></div>
          ))}
        </div>

        <div className="form-section">Personal & Arbeitszeit</div>
        <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr' }}>
          <div className="form-group"><label className="form-label">Beginn</label><input className="form-input" type="time" value={form.arbeitsbeginn} onChange={e => { set('arbeitsbeginn', e.target.value); calcHours(e.target.value, form.arbeitsende, form.anzahl_monteure); }} /></div>
          <div className="form-group"><label className="form-label">Ende</label><input className="form-input" type="time" value={form.arbeitsende} onChange={e => { set('arbeitsende', e.target.value); calcHours(form.arbeitsbeginn, e.target.value, form.anzahl_monteure); }} /></div>
          <div className="form-group"><label className="form-label">Std. gesamt</label><input className="form-input" value={form.std_gesamt} onChange={e => { set('std_gesamt', e.target.value); const n = parseFloat(form.anzahl_monteure) || 1; set('gesamtstunden', (parseFloat(e.target.value) * n).toFixed(1)); }} /></div>
          <div className="form-group"><label className="form-label">Monteure</label><input className="form-input" type="number" min="1" value={form.anzahl_monteure} onChange={e => { set('anzahl_monteure', e.target.value); const s = parseFloat(form.std_gesamt) || 0; if (s > 0) set('gesamtstunden', (s * parseFloat(e.target.value || 1)).toFixed(1)); }} /></div>
          <div className="form-group"><label className="form-label">Gesamtstunden MA</label><input className="form-input" value={form.gesamtstunden} onChange={e => set('gesamtstunden', e.target.value)} style={{ fontWeight: 600, color: 'var(--blue)' }} /></div>
        </div>
      </div>

      {/* Material */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-head">
          <div className="card-title">Materialeinlage laut LV</div>
          <select className="form-select" style={{ width: 'auto' }} value="" onChange={e => {
            const p = katalog.find(x => x.id === parseInt(e.target.value));
            if (!p || form.material.find(m => m.material_id === p.id)) return;
            setForm(f => ({ ...f, material: [...f.material, { material_id: p.id, pos_nr: p.pos_nr || '', bezeichnung: p.bezeichnung, einheit: p.einheit, menge: '', lage: '', bemerkung: '' }] }));
          }}>
            <option value="">+ Position hinzufügen</option>
            {katalog.filter(p => !form.material.find(m => m.material_id === p.id)).map(p => (
              <option key={p.id} value={p.id}>{p.pos_nr ? p.pos_nr + ' – ' : ''}{p.bezeichnung} ({p.bestand} {p.einheit})</option>
            ))}
          </select>
        </div>
        {form.material.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-3)', fontSize: 13 }}>Noch keine Positionen — oben auswählen</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Pos.-Nr.</th><th>Bezeichnung</th><th>Einh.</th><th style={{ width: 90 }}>Menge</th><th style={{ width: 150 }}>Lage</th><th>Bemerkung</th><th style={{ width: 40 }}></th></tr></thead>
              <tbody>
                {form.material.map((m, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--blue)', fontWeight: 600 }}>{m.pos_nr}</td>
                    <td>{m.bezeichnung}</td>
                    <td style={{ textAlign: 'center' }}>{m.einheit}</td>
                    <td><input className="form-input" value={m.menge} onChange={e => setMat(i, 'menge', e.target.value)} style={{ padding: '5px 8px' }} /></td>
                    <td><input className="form-input" value={m.lage} onChange={e => setMat(i, 'lage', e.target.value)} style={{ padding: '5px 8px' }} /></td>
                    <td><input className="form-input" value={m.bemerkung} onChange={e => setMat(i, 'bemerkung', e.target.value)} style={{ padding: '5px 8px' }} /></td>
                    <td><button type="button" onClick={() => setForm(f => ({ ...f, material: f.material.filter((_, j) => j !== i) }))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 18 }}>×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Leistungen & Checkboxen */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="form-section">Leistungen des Tages</div>
        <textarea className="form-textarea" style={{ minHeight: 100 }} value={form.leistungen} onChange={e => set('leistungen', e.target.value)} placeholder="Was wurde heute geleistet…" />
        <div className="form-section">Stillstand / Dokumentation</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
          {[['stillstand', 'Stillstand'], ['foto_doku', 'Fotodokumentation erstellt'], ['anfahrt', 'Anfahrtspauschale (50 €)']].map(([k, l]) => (
            <label key={k} className="check-group" style={{ background: 'var(--grey-bg)', padding: '10px 14px', borderRadius: 'var(--radius)', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!form[k]} onChange={e => set(k, e.target.checked)} /><span>{l}</span>
            </label>
          ))}
        </div>
        {form.stillstand && <div className="form-group"><label className="form-label">Grund für Stillstand</label><input className="form-input" value={form.stillstand_grund} onChange={e => set('stillstand_grund', e.target.value)} /></div>}
      </div>

      {/* Unterschriften */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="form-section">Unterschriften</div>
        <div className="form-grid form-grid-2">
          <div>
            <SignaturePad label="DV Elektro (Ausführender)" value={form.sig_ersteller} onChange={v => set('sig_ersteller', v)} />
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>{new Date().toLocaleDateString('de-DE')} — {user?.name}</div>
          </div>
          <div>
            <SignaturePad label="Bauleiter / Polier (Auftraggeber)" value={form.sig_bauleiter} onChange={v => set('sig_bauleiter', v)} />
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>{form.bauleiter || 'Bauleiter'}</div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        <button className="btn btn-secondary" onClick={() => navigate('/bautagebuch')}>Abbrechen</button>
        {isEdit && id && <button className="btn btn-secondary" onClick={exportPdf}>📄 PDF exportieren</button>}
        <button className="btn btn-secondary" onClick={() => save('entwurf')} disabled={saving}>Entwurf speichern</button>
        <button className="btn btn-primary" onClick={() => save('fertig')} disabled={saving}>{saving ? 'Speichert…' : 'Abschließen & speichern'}</button>
      </div>
    </div>
  );
}
